export const DASHBOARD_FEATURES = [
  { key: "overview", label: "Overview", hint: "Analytics dashboard", actions: ["view"] },
  { key: "visitors", label: "Visitors", hint: "Page-visit tracking", actions: ["view"] },
  { key: "contacts", label: "Messages", hint: "Contact inbox and replies", actions: ["view", "edit"] },
];

export const SITE_PAGES = [
  {
    key: "website",
    label: "Website",
    path: "/admin/content",
    sections: [
      { key: "org", label: "Contact details" },
      { key: "hero", label: "Home hero" },
      { key: "stats", label: "Home stats" },
      { key: "focus", label: "What we do" },
      { key: "services", label: "Services" },
      { key: "programmes", label: "Programmes" },
      { key: "events", label: "Events" },
      { key: "gallery", label: "Gallery" },
    ],
  },
  {
    key: "videos",
    label: "Videos",
    path: "/admin/videos",
    sections: [
      { key: "home", label: "Homepage video" },
      { key: "services", label: "Services videos" },
    ],
  },
];

export function emptyPermissions() {
  const next = {
    overview: { view: false },
    visitors: { view: false },
    contacts: { view: false, edit: false },
    pages: {},
  };
  SITE_PAGES.forEach((page) => {
    next.pages[page.key] = { access: false, sections: {} };
    page.sections.forEach((section) => {
      next.pages[page.key].sections[section.key] = { edit: false };
    });
  });
  return next;
}

export function allPermissions() {
  const next = emptyPermissions();
  next.overview.view = true;
  next.visitors.view = true;
  next.contacts = { view: true, edit: true };
  SITE_PAGES.forEach((page) => {
    next.pages[page.key].access = true;
    page.sections.forEach((section) => {
      next.pages[page.key].sections[section.key] = { edit: true };
    });
  });
  return next;
}

function sectionGranted(sectionPerms = {}) {
  return Boolean(sectionPerms.edit || sectionPerms.view);
}

export function normalizePermissions(raw) {
  const next = emptyPermissions();
  if (!raw || typeof raw !== "object") return next;

  DASHBOARD_FEATURES.forEach((feature) => {
    const src = raw[feature.key] || {};
    feature.actions.forEach((action) => {
      next[feature.key][action] = Boolean(src[action]);
    });
    if (next[feature.key].edit) next[feature.key].view = true;
  });

  const pagesIn = raw.pages && typeof raw.pages === "object" ? raw.pages : {};
  SITE_PAGES.forEach((page) => {
    const srcPage = pagesIn[page.key] || {};
    const srcSections = srcPage.sections || {};
    let anySection = false;
    page.sections.forEach((section) => {
      const src = srcSections[section.key] || {};
      const mapped = { edit: Boolean(src.edit || src === true) };
      if (mapped.edit) anySection = true;
      next.pages[page.key].sections[section.key] = mapped;
    });
    next.pages[page.key].access = Boolean(srcPage.access) || anySection;
  });

  return next;
}

function isLegacyUnrestricted(user) {
  const raw = user?.permissions;
  return !raw || typeof raw !== "object" || Object.keys(raw).length === 0;
}

export function hasPermission(user, feature, action = "view") {
  if (!user) return false;
  if (user.role === "superadmin") return true;
  if (feature === "admins") return false;
  if (isLegacyUnrestricted(user)) return true;

  const perms = normalizePermissions(user.permissions);

  if (feature === "overview" || feature === "visitors" || feature === "contacts") {
    if (action === "view") return Boolean(perms[feature]?.view || perms[feature]?.edit);
    return Boolean(perms[feature]?.[action]);
  }

  if (feature.includes(".")) {
    const [pageKey, sectionKey] = feature.split(".");
    const page = perms.pages[pageKey];
    const section = page?.sections?.[sectionKey];
    if (action === "view") return Boolean(page?.access || sectionGranted(section));
    return Boolean(section?.[action]);
  }

  const page = perms.pages[feature];
  if (page) {
    if (action === "view") {
      return Boolean(page.access || Object.values(page.sections || {}).some(sectionGranted));
    }
    return Object.values(page.sections || {}).some((section) => section[action]);
  }

  return false;
}

export function firstAllowedPath(user) {
  if (hasPermission(user, "overview", "view")) return "/admin";
  if (hasPermission(user, "visitors", "view")) return "/admin/visitors";
  if (hasPermission(user, "contacts", "view")) return "/admin/contacts";
  if (hasPermission(user, "website", "view")) return "/admin/content";
  if (hasPermission(user, "videos", "view")) return "/admin/videos";
  if (user?.role === "superadmin") return "/admin/admins";
  return null;
}

export function setDashboardPermission(permissions, featureKeyName, action, enabled) {
  const next = normalizePermissions(permissions);
  if (!next[featureKeyName]) return next;
  next[featureKeyName] = { ...next[featureKeyName], [action]: enabled };
  if (action === "edit" && enabled) next[featureKeyName].view = true;
  if (action === "view" && !enabled && "edit" in next[featureKeyName]) {
    next[featureKeyName].edit = false;
  }
  return next;
}

export function setPageAccess(permissions, pageKey, enabled) {
  const next = normalizePermissions(permissions);
  if (!next.pages[pageKey]) return next;
  next.pages[pageKey].access = enabled;
  const page = SITE_PAGES.find((item) => item.key === pageKey);
  if (!enabled) {
    Object.keys(next.pages[pageKey].sections).forEach((sectionKey) => {
      next.pages[pageKey].sections[sectionKey].edit = false;
    });
  } else {
    const any = Object.values(next.pages[pageKey].sections).some(sectionGranted);
    if (!any) {
      const first = page?.sections[0];
      if (first) next.pages[pageKey].sections[first.key].edit = true;
    }
  }
  return next;
}

export function setSectionPermission(permissions, pageKey, sectionKey, action, enabled) {
  const next = normalizePermissions(permissions);
  const section = next.pages[pageKey]?.sections?.[sectionKey];
  if (!section) return next;
  section[action] = enabled;
  if (enabled) next.pages[pageKey].access = true;
  if (!enabled && !Object.values(next.pages[pageKey].sections).some(sectionGranted)) {
    next.pages[pageKey].access = false;
  }
  return next;
}

export function setAllPageSections(permissions, pageKey, enabled) {
  const next = normalizePermissions(permissions);
  const page = SITE_PAGES.find((item) => item.key === pageKey);
  if (!page || !next.pages[pageKey]) return next;
  next.pages[pageKey].access = enabled;
  page.sections.forEach((section) => {
    next.pages[pageKey].sections[section.key] = { edit: enabled };
  });
  return next;
}
